import type { ReactNode } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './Modal.module.css';

export interface IModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Optional footer slot — rendered below the body separator when present. */
  footer?: ReactNode;
  children?: ReactNode;
  /**
   * Panel width. Defaults to 'md' (480px). Pass 'lg' for wider diff/review
   * modals (560px) or 'sm' for compact confirmations (360px).
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * DOM node to portal into. Defaults to `document.body`. Pass a node when the
   * modal must stay visible inside a native-fullscreen subtree (the Fullscreen
   * API paints only the fullscreen element, so a body portal is invisible).
   */
  container?: HTMLElement | null;
}

/**
 * Generic modal shell built on Base UI Dialog.
 * Wraps Dialog.Root / Portal / Backdrop / Popup / Title / Description so
 * consumers only provide content and footer actions.
 *
 * Pattern mirrors ui/DiscardModal — same tokens, same layout conventions.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  size = 'md',
  container,
}: IModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
    >
      <Dialog.Portal container={container ?? undefined}>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={`${styles.panel} ${styles[size]}`}>
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          {description && (
            <Dialog.Description className={styles.description}>
              {description}
            </Dialog.Description>
          )}
          {children && (
            <ScrollArea
              className={footer ? styles.body : `${styles.body} ${styles.bodyLast}`}
              viewportClassName={footer ? styles.bodyContent : `${styles.bodyContent} ${styles.bodyContentLast}`}
            >
              {children}
            </ScrollArea>
          )}
          {footer && <div className={styles.footer}>{footer}</div>}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
